import React from "react";
import { get, partial } from "lodash";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";

import {
  ADMIN_ROLE,
  ALLOWED_ROLES,
  hasAdminRole,
  isInternalSupportUser,
  isInternalTestUser,
} from "src/services/auth";
import { withAccess } from "src/components/Can";
import { isLocalEnvironment } from "src/services/localSettings";
import { isOrgDesignBetaEnabled } from "src/components/OrgChart/controller/dnd/featureFlags";

import PATHS, { PERSON_TAB, url } from "../../consts/urlPaths";
import { BAMBOOHR_OAUTH_CALLBACK_PATH } from "src/integrationTypes/components/bambooHROAuth";

import {
  CreateWorkspacePage,
  InternalDashboard,
  WorkspacesPage,
  WorkspaceConfigurationPage,
  AdminConfiguration,
  WorkspaceSettings,
  OrgDesignSettingsPage,
} from "../adminPages";

import { PAGE_TITLE_CONFIGS } from "../Helmet";

const ExplorePage = React.lazy(() => import("../../pages/ExplorePage"));
const TagDetailsPage = React.lazy(() => import("../../pages/TagDetailsPage"));
const PersonDetailPage = React.lazy(
  () => import("../../pages/PersonDetailPage")
);
const PersonDetailLinkPage = React.lazy(
  () => import("../../pages/PersonDetailLinkPage")
);
const TeamDetailPage = React.lazy(() => import("../../pages/TeamDetailPage"));
const AIDashboards = React.lazy(() => import("../../pages/AIDashboards"));
const AIDashboardsV2 = React.lazy(() => import("../../pages/AIDashboardsV2"));
const AIDashboardLibrary = React.lazy(
  () => import("../../pages/AIDashboardLibrary")
);
const TeamCanvasPage = React.lazy(() => import("../../pages/TeamCanvasPage"));
const InsightsPage = React.lazy(() => import("../../pages/InsightsPage"));
const PeopleListPage = React.lazy(() => import("../../pages/PeopleListPage"));
const WorkforcePage = React.lazy(() => import("../../pages/WorkforcePage"));
const ObjectivesPage = React.lazy(() => import("../../pages/ObjectivesPage"));
const TeamListPage = React.lazy(() => import("../../pages/TeamListPage"));
const SearchPage = React.lazy(() => import("src/pages/SearchPage"));
const WorkforcePlanningDashboard = React.lazy(
  () => import("../../pages/WorkforcePlanningDashboard")
);
const TagsExplore = React.lazy(() => import("../../pages/TagsExplore"));
const TeamsExplorer = React.lazy(() => import("../../pages/TeamsExplorer"));
const GoalsExplorer = React.lazy(() => import("../../pages/GoalsExplorer"));
const Logout = React.lazy(() => import("../../pages/Logout"));

const ApiDocs = React.lazy(() => import("../../pages/ApiDocs"));
const LoginHistoryPage = React.lazy(
  () => import("../../pages/LoginHistoryPage")
);

const Auth0UserReport = React.lazy(() => import("src/pages/Auth0UserReport"));

// Workflow pages
const WorkflowsPage = React.lazy(() => import("../../pages/WorkflowsPage"));
const WorkflowBuilderPage = React.lazy(
  () => import("../../pages/WorkflowBuilderPage")
);
const DesignsHub = React.lazy(() => import("../../pages/Designs/DesignsHub"));
const DesignDetailPage = React.lazy(
  () => import("../../pages/Designs/DesignDetailPage")
);
const DesignEditPage = React.lazy(
  () => import("../../pages/Designs/DesignEditPage")
);
const BambooHROAuthCallbackPage = React.lazy(
  () => import("../../pages/BambooHROAuthCallbackPage")
);

/**
 * Directory User Router Component
 * Renders PersonDetailPage and handles redirect to dashboard tab if no tabType specified
 * @param {Object} routeProps - Route props passed from React Router
 * @returns {React.Component} PersonDetailPage with ExplorePanel
 */
const DirectoryUserRouter = (routeProps) => {
  const { match } = routeProps;
  const hasTabType = match?.params?.tabType;

  // If we're at /home without a tabType, redirect to /home/dashboard
  if (!hasTabType) {
    return (
      <Redirect
        to={url(PATHS.DIRECTORY_USER_HOME, {
          tabType: PERSON_TAB.USER_DASHBOARD,
        })}
      />
    );
  }
  // Show the PersonDetailPage interface
  return <PersonDetailPage {...routeProps} />;
};

DirectoryUserRouter.propTypes = {
  // All props are forwarded to PersonDetailPage
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
};

const getRoutes = (
  props,
  userPerson,
  disablePersonalisation,
  enableDesigns = false,
  orgConfigLoading = false,
  enableDemoODOptions = false,
  workspaceFlagsLoading = false,
  enableAIChat = false,
  enableTeamCanvas = false,
  enableOrgDesign = false,
  enablePositionBackedVacancies = false,
  enableAIDataDrivenDashboards = false
) => {
  const { user } = props;

  const withAdminAccess = partial(withAccess, user, [ADMIN_ROLE]);
  const withDesignsAccess = partial(withAccess, user, ALLOWED_ROLES.DESIGNS);
  const noUserId = !get(userPerson, "aggregateId");
  const showDevTools =
    isInternalSupportUser(user) ||
    isInternalTestUser(user) ||
    isLocalEnvironment();
  // withAdminAccess cannot carry this: canPerform short-circuits true for
  // support users, so the admin requirement has to be checked here.
  const showWorkforce =
    hasAdminRole(user) &&
    isInternalSupportUser(user) &&
    !!enablePositionBackedVacancies;

  return [
    {
      exact: true,
      title: "Search",
      path: PATHS.SEARCH,
      Component: SearchPage,
    },
    {
      path: PATHS.TAG_EXPLORE,
      Component: TagsExplore,
    },
    {
      path: PATHS.TEAM_EXPLORE,
      Component: TeamsExplorer,
      title: "Teams Explorer",
    },
    {
      path: PATHS.GOALS_EXPLORE,
      Component: GoalsExplorer,
      title: "Goals Explorer",
    },
    {
      title: PAGE_TITLE_CONFIGS.tagDetailPage,
      path: PATHS.TAG_DETAILS,
      Component: TagDetailsPage,
    },
    {
      title: PAGE_TITLE_CONFIGS.teamDetailPage,
      path: PATHS.TEAM_DETAILS,
      Component: TeamDetailPage,
    },
    { exact: true, path: PATHS.LOGOUT, Component: Logout },
    {
      exact: true,
      title: "Api documentation",
      path: PATHS.API_DOCS,
      Component: ApiDocs,
    },
    {
      exact: true,
      title: "Login History",
      path: PATHS.LOGIN_HISTORY_PAGE,
      Component: LoginHistoryPage,
    },
    {
      exact: true,
      title: PAGE_TITLE_CONFIGS.personDetailPage,
      path: PATHS.PERSON_DETAILS,
      Component: PersonDetailPage,
    },
    {
      exact: true,
      title: "Redirecting",
      path: PATHS.PERSON_DETAILS_EMAIL_LINK,
      Component: PersonDetailLinkPage,
    },
    {
      exact: true,
      title: "Explore",
      path: PATHS.EXPLORE,
      Component: ExplorePage,
    },
    // Restricted routes
    {
      exact: true,
      isRestricted: true,
      title: "Insights",
      path: PATHS.INSIGHTS,
      Component: InsightsPage,
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled: !enableAIChat,
      title: "Dashboard Library",
      path: PATHS.AI_DASHBOARDS_LIBRARY,
      Component: AIDashboardLibrary,
    },
    {
      exact: true,
      isRestricted: true,
      // Data-driven canvas — workspace flag enableAIDataDrivenDashboards.
      isDisabled: !enableAIChat || !enableAIDataDrivenDashboards,
      title: "Dashboards",
      path: PATHS.AI_DASHBOARDS_V2,
      Component: AIDashboardsV2,
    },
    {
      exact: false,
      isRestricted: true,
      // AI dashboards route — gated on enableAIChat.
      isDisabled: !enableAIChat,
      title: "Dashboards",
      path: PATHS.AI_DASHBOARDS,
      Component: AIDashboards,
    },
    {
      exact: true,
      isRestricted: true,
      // Beta: release + OD beta + enableTeamCanvas.
      // Loading grace so deep links stay alive while flags resolve.
      isDisabled:
        !(
          isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions }) &&
          enableTeamCanvas
        ) && !workspaceFlagsLoading,
      title: "Team Canvas",
      path: PATHS.TEAM_CANVAS,
      Component: TeamCanvasPage,
    },
    {
      exact: true,
      isRestricted: true,
      // Before PEOPLE so /workforce/people is not parsed as workspaceId=workforce.
      isDisabled: !showWorkforce && !workspaceFlagsLoading,
      title: "Workforce",
      path: PATHS.WORKFORCE,
      Component: WorkforcePage,
    },
    {
      exact: true,
      isRestricted: true,
      title: "Teams",
      path: PATHS.TEAMS,
      Component: TeamListPage,
    },
    {
      exact: true,
      isRestricted: true,
      title: "People",
      path: PATHS.PEOPLE,
      Component: PeopleListPage,
    },
    {
      exact: true,
      isDisabled: disablePersonalisation || noUserId,
      isRestricted: true,
      title: PAGE_TITLE_CONFIGS.dashboard,
      path: PATHS.USER_HOME,
      Component: PersonDetailPage,
    },
    {
      exact: true,
      isRestricted: true,
      title: "Configuration",
      path: PATHS.DATASOURCE,
      Component: withAdminAccess(WorkspaceConfigurationPage, props),
    },
    {
      exact: true,
      title: "Workforce planning dashboard",
      path: PATHS.WORKFORCE_PLANNING_DASHBOARD,
      Component: WorkforcePlanningDashboard,
    },
    {
      exact: true,
      isRestricted: true,
      title: "Configuration",
      path: PATHS.WORKSPACE_CONFIGURATION,
      Component: withAdminAccess(WorkspaceConfigurationPage, props),
    },

    {
      exact: true,
      isRestricted: true,
      title: "New workspace",
      path: PATHS.CREATE_WORKSPACE,
      Component: withAdminAccess(CreateWorkspacePage, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "Workspaces",
      path: PATHS.WORKSPACES,
      Component: withAdminAccess(WorkspacesPage, props),
    },
    {
      isRestricted: true,
      title: "Objectives",
      path: PATHS.OBJECTIVES,
      Component: ObjectivesPage,
    },
    // Workflow routes
    {
      exact: true,
      isRestricted: true,
      title: "Workflows",
      path: PATHS.WORKFLOWS,
      Component: withAdminAccess(WorkflowsPage, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "Workflow Builder",
      path: PATHS.WORKFLOW_BUILDER,
      Component: withAdminAccess(WorkflowBuilderPage, props),
    },
    {
      exact: true,
      isRestricted: true,
      // Keep routes mounted while org config loads so deep links / refresh work
      isDisabled: !enableDesigns && !orgConfigLoading,
      title: "Designs",
      path: PATHS.DESIGNS,
      Component: withDesignsAccess(DesignsHub, props),
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled: !enableDesigns && !orgConfigLoading,
      title: "Edit design",
      path: PATHS.DESIGN_EDIT,
      Component: withDesignsAccess(DesignEditPage, props),
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled: !enableDesigns && !orgConfigLoading,
      title: "Design",
      path: PATHS.DESIGN_DETAIL,
      Component: withDesignsAccess(DesignDetailPage, props),
    },
    {
      exact: true,
      isDisabled: disablePersonalisation || noUserId,
      title: PAGE_TITLE_CONFIGS.dashboard,
      path: PATHS.DIRECTORY_USER_HOME,
      Component: DirectoryUserRouter,
    },
    {
      exact: true,
      isDisabled: disablePersonalisation || noUserId,
      title: PAGE_TITLE_CONFIGS.dashboard,
      path: PATHS.DIRECTORY_USER_HOME_BASE_PATH,
      Component: DirectoryUserRouter,
    },
    {
      exact: true,
      isDisabled: !showDevTools,
      isRestricted: true,
      title: "Internal dashboard",
      path: PATHS.INTERNAL_DASHBOARD,
      Component: InternalDashboard,
    },
    {
      exact: true,
      // Release: enableOrgDesign. Loading grace so deep links stay alive while
      // workspace flags resolve.
      isDisabled: !enableOrgDesign && !workspaceFlagsLoading,
      isRestricted: true,
      title: "Org Design settings",
      path: PATHS.ORG_DESIGN_SETTINGS,
      Component: OrgDesignSettingsPage,
    },
    {
      exact: true,
      isRestricted: true,
      title: "Tenant Settings",
      path: PATHS.ADMIN_DASHBOARD,
      Component: withAdminAccess(AdminConfiguration, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "Workspace Settings",
      path: PATHS.WORKSPACE_SETTINGS,
      Component: withAdminAccess(WorkspaceSettings, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "BambooHR OAuth",
      path: BAMBOOHR_OAUTH_CALLBACK_PATH,
      Component: withAdminAccess(BambooHROAuthCallbackPage, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "User Access Report",
      path: PATHS.USER_REPORT,
      Component: withAdminAccess(Auth0UserReport, props),
    },
  ];
};

export default getRoutes;
